import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Calculator,
  ChevronLeft,
  ChevronRight,
  Copy,
  Eye,
  FolderKanban,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { ColumnSelectorButton } from "@/components/ui/ColumnSelectorButton";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  useColumnVisibility,
  type ColumnDef,
} from "@/hooks/useColumnVisibility";

const ESTIMATION_COLUMNS: ColumnDef[] = [
  { key: "code", label: "#" },
  { key: "name", label: "Name" },
  { key: "project", label: "Project" },
  { key: "status", label: "Status" },
  { key: "items", label: "Items" },
  { key: "total", label: "Total" },
  { key: "version", label: "Version", defaultVisible: false },
  { key: "createdBy", label: "Created By", defaultVisible: false },
  { key: "date", label: "Date" },
];
import {
  useDeleteEstimation,
  useDuplicateEstimation,
  useEstimationList,
} from "../hooks/useEstimations";
import { NewEstimationDrawer } from "../components/NewEstimationDrawer";
import { formatCurrency } from "@/modules/expenses/components/CurrencyAmount";
import type { EstimationListItem } from "../types";

const BORDER = "hsl(210,30%,85%)";

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) + "…" : s;
}

export function EstimationStatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; fg: string; label: string }> = {
    draft: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)", label: "Draft" },
    review: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)", label: "Review" },
    approved: {
      bg: "hsl(142,55%,92%)",
      fg: "hsl(142,72%,28%)",
      label: "Approved",
    },
    archived: {
      bg: "hsl(38,92%,92%)",
      fg: "hsl(38,92%,30%)",
      label: "Archived",
    },
  };
  const s = styles[status] ?? styles.draft;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

const iconBtnBase =
  "inline-flex h-7 w-7 items-center justify-center rounded-md bg-transparent text-muted-foreground transition-[background,color] duration-150 hover:bg-muted hover:text-foreground";
const iconBtnDelete =
  "inline-flex h-7 w-7 items-center justify-center rounded-md bg-transparent text-muted-foreground transition-[background,color] duration-150 hover:bg-[hsl(0,70%,95%)] hover:text-[hsl(0,84%,50%)]";

export default function EstimationListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const {
    visibility,
    toggle: toggleColumn,
    reset: resetColumns,
    minReached,
  } = useColumnVisibility("estimations", ESTIMATION_COLUMNS);

  const { data, isLoading, isError, error } = useEstimationList({
    page,
    limit: pageSize,
    search: search || undefined,
    status: statusFilter || undefined,
  });

  const deleteEstimation = useDeleteEstimation();
  const duplicateEstimation = useDuplicateEstimation();

  const estimations = data?.data ?? [];
  const total = data?.pagination.total ?? 0;
  const totalPages = Math.max(1, data?.pagination.totalPages ?? 1);
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }

  function handleDelete(e: EstimationListItem) {
    setDeleteTarget({ id: e.id, name: e.name });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    await deleteEstimation.mutateAsync(deleteTarget.id);
    showToast("Estimation deleted");
  }

  async function handleDuplicate(e: EstimationListItem) {
    const res = await duplicateEstimation.mutateAsync(e.id);
    showToast(`Duplicated as ${res.estimation.code}`);
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Finance" },
        { label: "Cost Estimations" },
      ]}
    >
      {toast && (
        <div
          className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
          style={{ borderColor: "hsl(142,55%,80%)" }}
        >
          {toast}
        </div>
      )}

      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calculator
              className="h-[18px] w-[18px] text-foreground"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-semibold tracking-tight">
              Cost Estimations
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <ColumnSelectorButton
              columns={ESTIMATION_COLUMNS}
              visibility={visibility}
              onToggle={toggleColumn}
              onReset={resetColumns}
              minReached={minReached}
            />
            <Button onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              New Estimation
            </Button>
          </div>
        </div>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            <div className="font-medium">Failed to load estimations</div>
            <div className="mt-1 font-mono text-xs opacity-80">
              {(error as Error)?.message ?? "Unknown error"}
            </div>
          </div>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <div className="relative w-[280px] shrink-0">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Input
                  placeholder="Search estimations..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="pl-9"
                />
              </div>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className="w-40 shrink-0"
              >
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="review">Review</option>
                <option value="approved">Approved</option>
                <option value="archived">Archived</option>
              </Select>
            </div>

            <div
              className="overflow-hidden rounded-xl border bg-card"
              style={{ borderColor: BORDER }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                  <Loader2
                    className="mr-2 h-4 w-4 animate-spin"
                    strokeWidth={1.5}
                  />
                  Loading estimations…
                </div>
              ) : estimations.length === 0 ? (
                <div className="py-16 text-center text-sm text-muted-foreground">
                  No estimations match the current filters.
                </div>
              ) : (
                <table
                  className="w-full text-[13px]"
                  style={{ borderCollapse: "collapse" }}
                >
                  <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-[14px] py-[9px]" style={{ width: 110 }}>
                        #
                      </th>
                      <th className="px-[14px] py-[9px]">Name</th>
                      <th className="px-[14px] py-[9px]" style={{ width: 180 }}>
                        Project
                      </th>
                      <th className="px-[14px] py-[9px]" style={{ width: 100 }}>
                        Status
                      </th>
                      <th className="px-[14px] py-[9px]" style={{ width: 70 }}>
                        Items
                      </th>
                      <th className="px-[14px] py-[9px]" style={{ width: 150 }}>
                        Total Cost
                      </th>
                      <th className="px-[14px] py-[9px]" style={{ width: 70 }}>
                        Ver
                      </th>
                      <th className="px-[14px] py-[9px]" style={{ width: 140 }}>
                        Created By
                      </th>
                      <th className="px-[14px] py-[9px]" style={{ width: 110 }}>
                        Date
                      </th>
                      <th
                        className="px-[14px] py-[9px] text-right"
                        style={{ width: 140 }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {estimations.map((e) => (
                      <tr
                        key={e.id}
                        className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
                        onClick={() => navigate(`/estimations/${e.id}`)}
                      >
                        <td className="px-[14px] py-[11px] font-mono text-[12px] text-muted-foreground">
                          {e.code}
                        </td>
                        <td className="px-[14px] py-[11px]">
                          <Link
                            to={`/estimations/${e.id}`}
                            onClick={(ev) => ev.stopPropagation()}
                            className="font-medium text-foreground hover:text-[hsl(210,100%,35%)]"
                          >
                            {truncate(e.name, 50)}
                          </Link>
                        </td>
                        <td className="px-[14px] py-[11px] text-muted-foreground">
                          {e.projectName ? (
                            <span className="inline-flex items-center gap-1.5">
                              <FolderKanban
                                className="h-3 w-3"
                                strokeWidth={1.5}
                              />
                              <span className="truncate">
                                {e.projectName}
                              </span>
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-[14px] py-[11px]">
                          <EstimationStatusBadge status={e.status} />
                        </td>
                        <td className="px-[14px] py-[11px] text-muted-foreground">
                          {e.itemsCount}
                        </td>
                        <td className="px-[14px] py-[11px] text-foreground">
                          {formatCurrency(e.grandTotal, e.currency)}
                        </td>
                        <td className="px-[14px] py-[11px] text-muted-foreground">
                          v{e.version}
                        </td>
                        <td className="px-[14px] py-[11px] text-muted-foreground">
                          {e.createdByName ?? "—"}
                        </td>
                        <td className="px-[14px] py-[11px] text-muted-foreground">
                          {new Date(e.createdAt).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>
                        <td
                          className="relative px-[14px] py-[11px] text-right"
                          onClick={(ev) => ev.stopPropagation()}
                        >
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              className={iconBtnBase}
                              title="View"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                navigate(`/estimations/${e.id}`);
                              }}
                            >
                              <Eye
                                className="h-[15px] w-[15px]"
                                strokeWidth={1.5}
                              />
                            </button>
                            <button
                              type="button"
                              className={iconBtnBase}
                              title="Edit"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                navigate(`/estimations/${e.id}`);
                              }}
                            >
                              <Pencil
                                className="h-[15px] w-[15px]"
                                strokeWidth={1.5}
                              />
                            </button>
                            <button
                              type="button"
                              className={iconBtnBase}
                              title="Duplicate"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                handleDuplicate(e);
                              }}
                            >
                              <Copy
                                className="h-[15px] w-[15px]"
                                strokeWidth={1.5}
                              />
                            </button>
                            <button
                              type="button"
                              className={iconBtnDelete}
                              title="Delete"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                handleDelete(e);
                              }}
                            >
                              <Trash2
                                className="h-[15px] w-[15px]"
                                strokeWidth={1.5}
                              />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div
                className="flex items-center justify-between border-t bg-[hsl(210,30%,97%)] px-[14px] py-[9px] text-xs text-muted-foreground"
                style={{ borderColor: BORDER }}
              >
                <div className="flex items-center gap-3">
                  <span>
                    Showing {start}-{end} of {total} entries
                  </span>
                  <span className="flex items-center gap-1.5">
                    Show
                    <Select
                      value={String(pageSize)}
                      onChange={(ev) => {
                        setPageSize(Number(ev.target.value));
                        setPage(1);
                      }}
                      className="h-7 w-[68px] text-xs"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                    </Select>
                    entries
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <span className="px-2 text-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <NewEstimationDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={(id) => {
          setDrawerOpen(false);
          navigate(`/estimations/${id}`);
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete estimation?"
        description={
          deleteTarget
            ? `Are you sure you want to delete '${deleteTarget.name}'? It will be removed from your Estimations list.`
            : ""
        }
        onConfirm={confirmDelete}
      />
    </AppLayout>
  );
}
